import { ChangeEventHandler, Dispatch, FC, MouseEvent, SetStateAction, useCallback, useMemo, useState } from 'react';

import { AsyncSearch } from 'components';
import { searchTruckingCompanies } from 'services';
import { Booking, TruckingCompany } from 'types';

interface NewTruckingProps {
  booking: Partial<Booking>;
  setBooking: Dispatch<SetStateAction<Partial<Booking>>>;
}

const NewTrucking: FC<NewTruckingProps> = ({ booking, setBooking }) => {
  const [previous, setPrevious] = useState<TruckingCompany[]>([]);

  const anyTruckingCompany = useMemo(() => !booking.truckingCompanies || !booking.truckingCompanies.length, [booking.truckingCompanies]);

  const selectedIds: Set<number> = useMemo(() => {
    return new Set([...Object.assign([], booking.truckingCompanies).map(({ id }) => id)]);
  }, [booking.truckingCompanies]);

  const truckingCompanies = useMemo(() => {
    if (anyTruckingCompany) {
      return previous.map(({ name, scacCode }) => `${name} (${scacCode})`);
    } else {
      return booking.truckingCompanies?.map(({ name, scacCode }) => `${name} (${scacCode})`);
    }
  }, [anyTruckingCompany, booking.truckingCompanies, previous]);

  const toggleAnyTruckingCompany: ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      if (e.target.checked) {
        const copy = Object.assign([], booking.truckingCompanies);
        setPrevious(copy);
        setBooking(b => ({ ...b, truckingCompanies: null }));
      } else {
        const copy = Object.assign([], previous);
        setBooking(b => ({ ...b, truckingCompanies: copy }));
        setPrevious([]);
      }
    },
    [booking.truckingCompanies, previous, setBooking, setPrevious]
  );

  const addTruckingCompany = useCallback(
    (truckingCompany?: TruckingCompany) => {
      if (truckingCompany) {
        if (anyTruckingCompany) {
          const copy = Object.assign([], previous);
          setBooking(b => {
            if (selectedIds.has(truckingCompany.id)) return b;
            return { ...b, truckingCompanies: [...copy, truckingCompany] };
          });
          setPrevious([]);
        } else {
          setBooking(b => {
            const copy = Object.assign([], b.truckingCompanies);
            if (selectedIds.has(truckingCompany.id)) return b;
            return { ...b, truckingCompanies: [...copy, truckingCompany] };
          });
        }
      }
    },
    [anyTruckingCompany, previous, selectedIds, setBooking, setPrevious]
  );

  const removeTruckingCompany = useCallback(
    (index: number) => (e: MouseEvent) => {
      if (anyTruckingCompany) {
        setPrevious(p => {
          const copy = [...p];
          if (index in copy) copy.splice(index, 1);
          return copy;
        });
      } else {
        setBooking(b => {
          const copy = Object.assign([], b.truckingCompanies);
          if (index in copy) copy.splice(index, 1);
          return { ...b, truckingCompanies: !copy.length ? null : copy };
        });
      }
    },
    [anyTruckingCompany, setBooking, setPrevious]
  );

  return (
    <div id="trucking-companies" className="grid-10 p-2">
      <div className="d-flex flex-column align-items-center">
        <h6 className="text-info-dark">ANY</h6>
        <div className="mt-2 p-2 w-100">
          <input className="w-100" type="checkbox" onChange={toggleAnyTruckingCompany} checked={anyTruckingCompany} />
        </div>
      </div>
      <div className="col-span-3">
        <h6 className="text-info-dark">TRUCKING COMPANY (SCAC)</h6>
        <div className="mt-2 w-100">
          <AsyncSearch<TruckingCompany>
            formatOption={({ name, scacCode }) => `${name} (${scacCode})`}
            formatValue={truckingCompany => truckingCompany?.name ?? ''}
            handleSelect={addTruckingCompany}
            search={searchTruckingCompanies}
            selected={selectedIds}
          />
        </div>
      </div>
      <div className="col-span-6 d-flex flex-wrap g-2 p-5">
        {truckingCompanies?.map((t, i) => (
          <div className={`chip d-flex ${anyTruckingCompany ? 'inactive' : 'active'}`} key={t}>
            {t}
            <svg viewBox="0 0 24 24" onClick={removeTruckingCompany(i)}>
              <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
            </svg>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NewTrucking;
